import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Seo } from "@/components/site/Seo";
import { TreasureChest } from "@/components/portal/TreasureChest";
import { ReferralCard } from "@/components/portal/ReferralCard";
import { RewardsCatalog } from "@/components/portal/RewardsCatalog";
import { FamilyManager } from "@/components/portal/FamilyManager";
import { ManageMembership } from "@/components/portal/ManageMembership";
import { LeaveReviewButton } from "@/components/site/GoogleReviews";

export default function Portal() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [referral, setReferral] = useState(null);

  const load = useCallback(() => {
    api.get("/portal/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/portal/referral").then((r) => setReferral(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const doLogout = async () => { await logout(); navigate("/"); };

  const redeem = async (reward) => {
    try {
      await api.post("/portal/redeem", { reward_id: reward.id });
      toast.success(`Requested ${reward.name} — the front desk will confirm on your next visit.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not redeem this reward");
    }
  };

  if (!summary) return <div className="min-h-screen bg-linen grid place-items-center text-taupe" data-testid="portal-loading">Loading your rewards…</div>;

  const children = (summary.family || []).filter((m) => m.is_child);
  const firstName = (summary.user?.name || "").split(" ")[0] || "there";

  return (
    <div className="min-h-screen bg-linen" data-testid="patient-portal">
      <Seo title="My Rewards | Psalm 30:2 Chiropractic" />

      <header className="bg-espresso text-ivory">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-ivory/70 hover:text-ivory text-xs uppercase tracking-[0.16em] transition-colors" data-testid="portal-back">
            <ArrowLeft size={15} /> Back to site
          </Link>
          <button onClick={doLogout} data-testid="portal-logout" className="inline-flex items-center gap-2 text-ivory/70 hover:text-ivory text-xs uppercase tracking-[0.16em] transition-colors">
            Sign out <LogOut size={15} />
          </button>
        </div>
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pb-14 pt-4">
          <div className="overline !text-brass mb-3">Your Rewards</div>
          <h1 className="font-serif font-light text-4xl lg:text-6xl" data-testid="portal-greeting">Hello, {firstName}.</h1>
          {summary.membership?.status === "active" && (
            <div className="mt-5 inline-flex items-center gap-2 bg-brass/20 border border-brass/40 rounded-full px-4 py-2 text-xs uppercase tracking-[0.16em] text-brass" data-testid="portal-membership-badge">
              <span className="h-1.5 w-1.5 rounded-full bg-brass" /> {summary.membership.plan_name} Member
            </div>
          )}
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-14 space-y-16">
        <section data-testid="portal-membership">
          <div className="overline mb-2">Membership</div>
          <h2 className="font-serif font-light text-3xl lg:text-4xl text-espresso mb-8">Your plan & perks</h2>
          <ManageMembership />
        </section>

        <section data-testid="portal-rewards">
          <RewardsCatalog points={summary.points} rewards={summary.rewards} onRedeem={redeem} />
        </section>

        {children.length > 0 && (
          <section data-testid="portal-treasure">
            <div className="overline mb-2">For the little ones</div>
            <h2 className="font-serif font-light text-3xl lg:text-4xl text-espresso mb-8">Treasure Chest</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {children.map((c) => <TreasureChest key={c.id} child={c} />)}
            </div>
          </section>
        )}

        <section data-testid="portal-referral-section">
          <ReferralCard referral={referral} />
        </section>

        <section data-testid="portal-family-section">
          <FamilyManager family={summary.family} onChange={load} />
        </section>

        <section data-testid="portal-review-section" className="bg-ivory border border-espresso/10 rounded-sm p-8 lg:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="overline mb-3">Love your care?</div>
            <h2 className="font-serif font-light text-3xl text-espresso">Help another family find us.</h2>
            <p className="mt-2 text-espressosoft/75 font-light">A quick Google review means the world to our small practice.</p>
          </div>
          <LeaveReviewButton testid="portal-leave-review" />
        </section>
      </main>
    </div>
  );
}
